// Enhanced Google Authentication Service with One Tap support

class GoogleAuthService {
  constructor() {
    this.initialized = false;
    this.clientId = null;
    this.oneTapEnabled = true;
    this.autoSignInAttempted = false;
  }

  async initialize(options = {}) {
    if (this.initialized) return;

    try {
      this.clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      
      if (!this.clientId) {
        throw new Error('Google Client ID not found');
      }

      await this.waitForGoogleAPI();
      
      window.google.accounts.id.initialize({
        client_id: this.clientId,
        callback: this.handleCredentialResponse.bind(this),
        auto_select: options.autoSelect !== false,
        cancel_on_tap_outside: false,
      });

      this.initialized = true;
      
      // Show One Tap prompt if enabled and not already attempted
      if (this.oneTapEnabled && !this.autoSignInAttempted && options.showOneTap !== false) {
        this.showOneTap();
      }
    } catch (error) {
      console.error('Failed to initialize Google Auth:', error);
      throw error;
    }
  }

  waitForGoogleAPI() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Google API failed to load'));
      }, 10000);

      const checkGoogle = () => {
        if (window.google?.accounts?.id) {
          clearTimeout(timeout);
          resolve();
        } else {
          setTimeout(checkGoogle, 100);
        }
      };

      checkGoogle();
    });
  }

  handleCredentialResponse(response) {
    if (response?.credential) {
      window.dispatchEvent(new CustomEvent('googleLoginSuccess', {
        detail: { token: response.credential }
      }));
    } else {
      window.dispatchEvent(new CustomEvent('googleOneTapFailed'));
    }
  }

  async renderButton(element, options = {}) {
    await this.initialize({ showOneTap: false });
    
    if (!element || !window.google?.accounts?.id) {
      throw new Error('Cannot render button');
    }

    const buttonOptions = {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      ...options
    };

    window.google.accounts.id.renderButton(element, buttonOptions);
  }

  showOneTap() {
    if (!this.initialized || this.autoSignInAttempted) return;
    
    try {
      this.autoSignInAttempted = true;
      
      // Simple prompt without notification callback to avoid FedCM warnings
      window.google.accounts.id.prompt();
      
      // Set a timeout to handle cases where One Tap doesn't appear or fails
      setTimeout(() => {
        if (!localStorage.getItem('authToken')) {
          window.dispatchEvent(new CustomEvent('googleOneTapFailed'));
        }
      }, 10000); // 10 second timeout for One Tap to complete
      
    } catch (error) {
      console.error('Error showing One Tap:', error);
      window.dispatchEvent(new CustomEvent('googleOneTapFailed'));
    }
  }

  disableOneTap() {
    this.oneTapEnabled = false;
    if (window.google?.accounts?.id) {
      try {
        window.google.accounts.id.cancel();
      } catch (error) {
        console.error('Error canceling One Tap:', error);
      }
    }
  }

  enableOneTap() {
    this.oneTapEnabled = true;
    this.autoSignInAttempted = false;
  }

  async initializeOneTap() {
    await this.initialize({ showOneTap: true });
  }

  async forceShowOneTap() {
    this.autoSignInAttempted = false;
    await this.initialize({ showOneTap: false });
    window.google.accounts.id.prompt();
  }
}

export const googleAuthService = new GoogleAuthService();
export default googleAuthService;